/**
 * Central API error bus — one funnel for every query/mutation failure.
 *
 * Deliberately UI-agnostic: the app has no global <Toaster/> today, so
 * the default behaviour is (1) normalize the error to a human-readable
 * Arabic string, (2) emit a `mesp:query-error` CustomEvent any layer
 * can subscribe to, (3) breadcrumb to Sentry. Flip
 * REACT_APP_QUERY_TOAST_ERRORS=1 to additionally surface sonner toasts
 * once a Toaster is mounted — zero code changes needed.
 */
import * as Sentry from '@sentry/react';
import { toErrorText } from '../toErrorText';
import { TOAST_GLOBAL_ERRORS } from './config';

export const QUERY_ERROR_EVENT = 'mesp:query-error';

const isAuthError = (error) => error?.response?.status === 401;

export function reportQueryError(error, context = {}) {
  // 401s are owned by the axios refresh interceptor + AuthContext —
  // never double-report them globally.
  if (isAuthError(error)) return;

  const message = toErrorText(error);

  try {
    window.dispatchEvent(new CustomEvent(QUERY_ERROR_EVENT, {
      detail: { message, status: error?.response?.status ?? null, ...context },
    }));
  } catch (_e) { /* non-browser guard */ }

  try {
    Sentry.addBreadcrumb({
      category: 'react-query',
      level: 'error',
      message,
      data: { ...context },
    });
  } catch (_e) { /* Sentry not initialized */ }

  if (TOAST_GLOBAL_ERRORS) {
    // Lazy import keeps sonner out of the critical path when disabled.
    import('sonner').then(({ toast }) => toast.error(message)).catch(() => {});
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[query-error]', message, context, error);
  }
}
